import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, imageUrl, formatPrice, formatKm } from "../lib/api";
import { usePublicSettings } from "../lib/publicSettings";
import { useWishlist } from "../lib/wishlist";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { ReservationDialog } from "../components/ReservationDialog";
import { toast } from "sonner";
import { ArrowLeft, ChevronLeft, ChevronRight, Phone, Mail, Lock, Share2, MessageCircle, Facebook, Link as LinkIcon, Heart, CheckCircle2, Youtube } from "lucide-react";
import { CarCard } from "../components/CarCard";
import { StoryVideo } from "../components/StoryVideo";

// Extract YouTube video ID from various URL formats
const parseYoutubeId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = String(url).match(p);
    if (m) return m[1];
  }
  return null;
};

export default function CarDetail() {
  const { id } = useParams();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const [resOpen, setResOpen] = useState(false);
  const [wishlistCount, setWishlistCount] = useState(0);
  const settings = usePublicSettings();
  const contactPhone = settings?.contact_phone || "";
  const contactEmail = settings?.contact_email || "";
  const { has, toggle } = useWishlist();
  const saved = car ? has(car.id) : false;

  // Share URL points to backend endpoint which serves OG tags for social scrapers
  // and redirects human browsers to /auto/{id}.
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const shareUrl = car ? `${backendUrl}/api/share/${car.id}` : "";
  const shareTitle = car ? `${car.brand} ${car.model} (${car.year})${car.ref_code ? ` · Rif. ${car.ref_code}` : ""} · Guzzardella Auto` : "";

  const shareWhatsapp = () => {
    const text = encodeURIComponent(`${shareTitle}\n${shareUrl}`);
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener,noreferrer");
  };
  const shareFacebook = () => {
    const u = encodeURIComponent(shareUrl);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${u}`, "_blank", "noopener,noreferrer,width=600,height=500");
  };
  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, url: shareUrl });
      } catch { /* user cancelled */ }
    } else {
      copyLink();
    }
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copiato negli appunti");
    } catch {
      toast.error("Impossibile copiare il link");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/cars/${id}`);
        setCar(r.data);
      } catch (e) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
    (async () => {
      try {
        const r = await api.get(`/cars/${id}/wishlist-count`);
        setWishlistCount(r.data?.count || 0);
      } catch { /* ignore */ }
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="App min-h-screen">
        <Header />
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="h-96 bg-muted animate-pulse" />
        </div>
      </div>
    );
  }
  if (notFound || !car) {
    return (
      <div className="App min-h-screen flex flex-col">
        <Header />
        <div className="max-w-7xl mx-auto px-6 py-24 flex-1 text-center">
          <h2 className="font-display text-4xl mb-4">Auto non trovata</h2>
          <Link to="/" className="text-gold underline">Torna al parco auto</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const images = car.images || [];
  const active = images[activeImg];
  const optionals = Array.isArray(car.optionals) ? car.optionals : [];
  const youtubeId = parseYoutubeId(car.youtube_url);

  return (
    <div className="App min-h-screen flex flex-col" data-testid="car-detail-page">
      <Header />

      <div className="max-w-7xl mx-auto px-5 md:px-10 pt-8 md:pt-12 w-full">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-midnight/60 hover:text-midnight mb-8"
          data-testid="back-to-inventory"
        >
          <ArrowLeft size={14} /> Torna al parco auto
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14">
          <div className="lg:col-span-3">
            <div className="relative aspect-[4/3] bg-midnight overflow-hidden border border-border" data-testid="car-gallery-main">
              {active ? (
                <img
                  src={imageUrl(active.storage_path)}
                  alt={`${car.brand} ${car.model}`}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/40">
                  Nessuna foto disponibile
                </div>
              )}
              {images.length > 1 && (
                <>
                  <button
                    onClick={() => setActiveImg((activeImg - 1 + images.length) % images.length)}
                    className="absolute left-3 bottom-3 h-10 w-10 flex items-center justify-center bg-white/70 hover:bg-gold border border-white/40 text-midnight opacity-70 hover:opacity-100 transition-opacity backdrop-blur-sm rounded-sm shadow-lg"
                    data-testid="gallery-prev"
                    aria-label="Foto precedente"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    onClick={() => setActiveImg((activeImg + 1) % images.length)}
                    className="absolute right-3 bottom-3 h-10 w-10 flex items-center justify-center bg-white/70 hover:bg-gold border border-white/40 text-midnight opacity-70 hover:opacity-100 transition-opacity backdrop-blur-sm rounded-sm shadow-lg"
                    data-testid="gallery-next"
                    aria-label="Foto successiva"
                  >
                    <ChevronRight size={18} />
                  </button>
                </>
              )}
              {car.is_sold && (
                <div className="absolute top-4 left-4 px-4 py-2 bg-midnight text-white text-xs uppercase tracking-widest">
                  Venduta
                </div>
              )}
            </div>
            {images.length > 1 && (
              <div className="grid grid-cols-5 gap-2 mt-3" data-testid="car-thumbnails">
                {images.map((img, i) => (
                  <button
                    key={img.id}
                    onClick={() => setActiveImg(i)}
                    className={`aspect-square overflow-hidden border-2 transition-colors ${
                      i === activeImg ? "border-gold" : "border-transparent"
                    }`}
                  >
                    <img src={imageUrl(img.storage_path)} className="w-full h-full object-cover" alt="" />
                  </button>
                ))}
              </div>
            )}
            {car.story_video_path && (
              <div className="mt-6 hidden lg:block">
                <StoryVideo
                  videoPath={car.story_video_path}
                  contentType={car.story_video_content_type}
                  poster={car.images?.[0] ? imageUrl(car.images[0].storage_path) : undefined}
                />
              </div>
            )}
          </div>

          <div className="lg:col-span-2">
            <p className="text-xs uppercase tracking-[0.3em] text-midnight/50 mb-3">{car.brand}</p>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-midnight leading-tight mb-4" data-testid="car-title">
              {car.model}
            </h1>
            {car.story_video_path && (
              <div className="mb-6 lg:hidden">
                <StoryVideo
                  videoPath={car.story_video_path}
                  contentType={car.story_video_content_type}
                  poster={car.images?.[0] ? imageUrl(car.images[0].storage_path) : undefined}
                />
              </div>
            )}
            {car.is_on_sale && !car.is_sold && !car.is_reserved && (
              <div className="mb-4 flex items-center gap-2">
                <span className="px-3 py-1 bg-gold text-midnight text-xs uppercase tracking-widest font-bold" data-testid="car-sale-badge">
                  {car.sale_label || "Promozione"}
                </span>
              </div>
            )}
            <div className="flex items-baseline gap-3 mb-8">
              {car.is_on_sale && car.sale_price && car.sale_price < car.price ? (
                <>
                  <span className="font-display text-4xl md:text-5xl font-bold text-gold-outlined" data-testid="car-price">
                    {formatPrice(car.sale_price)}
                  </span>
                  <span className="font-display text-2xl text-midnight/40 line-through" data-testid="car-original-price">
                    {formatPrice(car.price)}
                  </span>
                </>
              ) : (
                <span className="font-display text-4xl md:text-5xl font-bold text-gold-outlined" data-testid="car-price">
                  {formatPrice(car.price)}
                </span>
              )}
            </div>

            {(car.is_reserved || car.is_sold) && (
              <div className={`mb-6 p-4 border ${car.is_sold ? "bg-white/60 border-midnight/20" : "bg-orange-50 border-orange-300"}`}>
                <p className={`text-sm font-semibold uppercase tracking-widest ${car.is_sold ? "text-midnight" : "text-orange-700"}`}>
                  {car.is_sold ? "Auto venduta" : "Auto prenotata"}
                </p>
                <p className="text-xs text-midnight/60 mt-1">
                  {car.is_sold
                    ? "Questa auto non è più disponibile all'acquisto."
                    : "Un cliente ha versato una caparra. Contatta Rocco per lista d'attesa."}
                </p>
              </div>
            )}

            {!car.is_sold && (
              <button
                onClick={() => setResOpen(true)}
                disabled={car.is_reserved}
                className="btn-gold w-full flex items-center justify-center gap-2 py-4 mb-4 text-xs uppercase tracking-widest font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="reserve-car-btn"
              >
                <Lock size={14} />
                {car.is_reserved ? "Già Prenotata" : "Blocca con Caparra"}
              </button>
            )}

            {wishlistCount >= 3 && (
              <div
                className="mb-4 flex items-center gap-2 px-4 py-3 border border-gold/40 bg-gold/10 text-sm text-midnight"
                data-testid="car-wishlist-social-proof"
              >
                <Heart size={16} className="text-gold" fill="currentColor" />
                <span>
                  <span className="font-bold text-gold">{wishlistCount} persone</span>{" "}
                  hanno salvato quest'auto — è tra le più richieste
                </span>
              </div>
            )}

            <button
              type="button"
              onClick={() => toggle(car.id)}
              className={`w-full flex items-center justify-center gap-2 py-3 mb-6 text-xs uppercase tracking-widest font-semibold border transition-colors ${
                saved
                  ? "bg-gold/10 border-gold text-gold"
                  : "border-midnight/25 text-midnight hover:border-gold hover:text-gold"
              }`}
              data-testid="wishlist-toggle-detail"
              aria-pressed={saved}
            >
              <Heart size={14} fill={saved ? "currentColor" : "none"} strokeWidth={2.2} />
              {saved ? "Nei preferiti" : "Aggiungi ai preferiti"}
            </button>

            <div className="card-sharp p-6 bg-gradient-to-br from-white to-slate-100 text-midnight mb-6 border border-midnight/10" data-testid="interested-panel">
              <h3 className="font-display text-lg font-semibold mb-4 text-midnight">Interessato?</h3>
              <p className="text-sm text-midnight/70 mb-6 leading-relaxed">
                Contatta Rocco direttamente per una prova su strada o maggiori informazioni.
              </p>
              <div className="space-y-3 mb-6">
                <a
                  href={contactPhone ? `tel:${contactPhone.replace(/\s+/g, "")}` : "#"}
                  onClick={(e) => { if (!contactPhone) e.preventDefault(); }}
                  className={`btn-gold w-full flex items-center justify-center gap-2 px-4 py-3 text-xs uppercase tracking-widest font-semibold ${!contactPhone ? "opacity-50 cursor-not-allowed" : ""}`}
                  data-testid="contact-phone"
                >
                  <Phone size={14} /> {contactPhone ? "Chiama Ora" : "Telefono non disponibile"}
                </a>
                <a
                  href={contactEmail ? `mailto:${contactEmail}` : "#"}
                  onClick={(e) => { if (!contactEmail) e.preventDefault(); }}
                  className={`w-full flex items-center justify-center gap-2 px-4 py-3 text-xs uppercase tracking-widest font-semibold border border-midnight/30 text-midnight transition-colors ${contactEmail ? "hover:border-gold hover:text-gold" : "opacity-50 cursor-not-allowed"}`}
                  data-testid="contact-email"
                >
                  <Mail size={14} /> {contactEmail ? "Invia Email" : "Email non disponibile"}
                </a>
              </div>
              <div className="pt-5 border-t border-midnight/10">
                <div className="flex items-center gap-2 mb-3">
                  <Share2 size={14} className="text-gold" />
                  <h4 className="font-display text-sm font-semibold uppercase tracking-widest text-midnight">Condividi</h4>
                </div>
                <p className="text-xs text-midnight/60 mb-4 leading-relaxed">
                  Condividi questa auto: apparirà con foto, titolo e prezzo direttamente in chat.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={shareWhatsapp}
                    className="flex items-center justify-center gap-2 py-2.5 text-[11px] uppercase tracking-widest font-semibold text-white bg-[#25D366] hover:bg-[#1FB855] transition-colors"
                    data-testid="share-whatsapp-btn"
                    aria-label="Condividi su WhatsApp"
                  >
                    <MessageCircle size={13} strokeWidth={2.5} /> WhatsApp
                  </button>
                  <button
                    onClick={shareFacebook}
                    className="flex items-center justify-center gap-2 py-2.5 text-[11px] uppercase tracking-widest font-semibold text-white bg-[#1877F2] hover:bg-[#1467D1] transition-colors"
                    data-testid="share-facebook-btn"
                    aria-label="Condividi su Facebook"
                  >
                    <Facebook size={13} strokeWidth={2.5} /> Facebook
                  </button>
                  <button
                    onClick={copyLink}
                    className="flex items-center justify-center gap-2 py-2.5 text-[11px] uppercase tracking-widest font-semibold text-midnight border border-midnight/25 hover:border-gold hover:text-gold transition-colors"
                    data-testid="share-copy-btn"
                    aria-label="Copia link"
                  >
                    <LinkIcon size={13} strokeWidth={2.5} /> Copia link
                  </button>
                  <button
                    onClick={shareNative}
                    className="flex items-center justify-center gap-2 py-2.5 text-[11px] uppercase tracking-widest font-semibold text-midnight border border-midnight/25 hover:border-gold hover:text-gold transition-colors"
                    data-testid="share-native-btn"
                    aria-label="Altre app"
                  >
                    <Share2 size={13} strokeWidth={2.5} /> Altre app
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {youtubeId && (
          <div className="mt-16 max-w-5xl" data-testid="car-video">
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="divider-gold" />
              <span className="text-gold text-xs uppercase tracking-[0.3em] font-medium flex items-center gap-2">
                <Youtube size={14} /> Video Auto
              </span>
            </div>
            <div className="relative w-full bg-midnight border border-border" style={{ paddingBottom: "56.25%" }}>
              <iframe
                src={`https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1`}
                title={`Video ${car.brand} ${car.model}`}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
                data-testid="youtube-embed"
              />
            </div>
          </div>
        )}

        {optionals.length > 0 && (
          <div className="mt-16 max-w-5xl" data-testid="car-optionals">
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="divider-gold" />
              <span className="text-gold text-xs uppercase tracking-[0.3em] font-medium">Dotazione & Optional</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-2">
              {optionals.map((opt) => (
                <div
                  key={opt}
                  className="flex items-center gap-2 py-2 border-b border-border/60 last:border-0"
                  data-testid={`optional-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <CheckCircle2 size={14} className="text-gold shrink-0" />
                  <span className="text-sm text-gold font-semibold">{opt}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {car.description && (
          <div className="mt-16 max-w-4xl">
            <div className="inline-flex items-center gap-3 mb-4">
              <span className="divider-gold" />
              <span className="text-gold text-xs uppercase tracking-[0.3em] font-medium">Descrizione</span>
            </div>
            <p className="text-midnight leading-relaxed whitespace-pre-line text-base md:text-lg font-medium" data-testid="car-description">
              {car.description}
            </p>
          </div>
        )}

        <SimilarCars carId={car.id} />
      </div>

      <ReservationDialog open={resOpen} onOpenChange={setResOpen} car={car} />

      <Footer />
    </div>
  );
}

const SimilarCars = ({ carId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/cars/${carId}/similar`, { params: { limit: 3 } });
        if (alive) setItems(Array.isArray(r.data) ? r.data : []);
      } catch {
        if (alive) setItems([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [carId]);

  if (loading || items.length === 0) return null;
  return (
    <div className="mt-20" data-testid="similar-cars-section">
      <div className="inline-flex items-center gap-3 mb-6">
        <span className="divider-gold" />
        <span className="text-gold text-xs uppercase tracking-[0.3em] font-medium">Auto simili</span>
      </div>
      <p className="text-midnight/70 mb-8 max-w-2xl">
        Potrebbero interessarti anche queste vetture selezionate del nostro parco.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((c, i) => (
          <CarCard key={c.id} car={c} index={i} />
        ))}
      </div>
    </div>
  );
};
